import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Home,
  UserCircle,
  QrCode,
  Calendar,
  Award,
  Settings,
  Headphones,
  Music,
  LogOut,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "./Avatar";
import packageJson from "../../../package.json";
const version = packageJson.version;

const menuItems = [
  { id: "home", label: "Home", icon: Home, route: "/loggedinartist" },
  { id: "qr", label: "QR Code", icon: QrCode, route: "/me" },
  { id: "calendar", label: "Calendar", icon: Calendar, route: "/bookevent" },
  { id: "rewards", label: "Rewards", icon: Award, route: "/rewards" },
  {
    id: "profile",
    label: "Profile",
    icon: UserCircle,
    route: "/artistprofile",
  },
  // { id: "settings", label: "Settings", icon: Settings },
];

const Sidebar = ({ currentPage, onPageChange }) => {
  const navigate = useNavigate();
  // Only animate sidebar for home page
  const SidebarContent = (
    <div className="fixed left-0 top-0 h-full w-80 bg-gradient-to-b from-logo via-[#6720A3] to-[#6D2398] text-white shadow-2xl z-20 flex flex-col">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5 animate-pulse"
            style={{
              left: `${(i * 23) % 90}%`,
              top: `${(i * 37) % 85}%`,
              width: `${(i % 3) * 10 + 15}px`,
              height: `${(i % 3) * 10 + 15}px`,
              animationDuration: `${3 + (i % 4)}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 p-6 flex flex-col flex-1">
        {/* Logo - styled like EventDetails header */}
        <div className="mb-8 flex items-center justify-center">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold mr-1">TRINGBO</h1>
            <img
              src="/images/logo.svg"
              className="h-[1em] align-middle py-2 mb-2"
              alt="Logo"
              style={{ display: "inline", height: 40 }}
            />
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;

            return (
              <motion.button
                key={item.id}
                onClick={() => {
                  if (onPageChange) onPageChange(item.id);
                  if (item.route && item.id === "rewards") navigate(item.route);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-300 ${
                  isActive
                    ? "bg-white/20 text-white shadow-lg backdrop-blur-sm"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.02,
                  x: 5,
                  boxShadow: isActive
                    ? "0 5px 15px rgba(255,255,255,0.2)"
                    : "none",
                }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className={`p-2 rounded-lg ${isActive ? "bg-white/20" : ""}`}
                >
                  <Icon className="h-5 w-5" />
                </motion.div>
                <span className="font-medium">{item.label}</span>

                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="ml-auto h-2 w-2 rounded-full bg-white"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </nav>

        {/* Footer with Red Logout Button and Version */}
        <div className="mt-auto pt-6 pb-2 w-full flex flex-col items-center gap-3">
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
            type="button"
            className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-400/30 text-sm font-bold transition-all shadow-sm active:scale-95 group"
          >
            <LogOut className="w-4 h-4 text-red-300 group-hover:text-red-200 transition-colors" />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    </div>
  );
  return SidebarContent;
};

export default Sidebar;
